import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { FiUpload, FiFile, FiImage, FiX, FiZoomIn, FiGlobe } from "react-icons/fi";
import { publicRequest } from "../../requestMethods";
import StepCodeTag from "../../components/shared/StepCodeTag";
import { CUSTOMER_DRAFT_STEP_CODES } from "../../constants/workflowStepCodes";

const translations = {
    en: {
        title: "Project Drafts",
        subtitle: "View and review project drafts",
        order: "Order",
        customer: "Customer",
        approvedOn: "Approved on",
        approveButton: "Customer Approve",
        approvedButton: "Customer Approved",
        saving: "Saving...",
        approvalSaved: "Customer approval saved successfully.",
        approvalSaveFailed: "Failed to save customer approval. Please try again.",
        checkAllDrafts: "Please tick all draft files before approving.",
        signatureRequired: "Please provide a signature before approving.",
        approvalRequirements: "Approval requirements",
        tickAllDrafts: "Tick all draft files",
        provideSignature: "Provide customer signature",
        draftFiles: "Draft Files",
        file: "file",
        files: "files",
        noProductLinked: "No product linked",
        noDraftsTitle: "No Drafts Available",
        noDraftsSubtitle: "No drafts have been uploaded for this order yet.",
        signatureTitle: "Customer Signature",
        savedSignatureTitle: "Saved Signature",
        clearSignature: "Clear Signature",
        signatureHint: "Sign above using your mouse or touch before approving.",
        noSavedSignature: "No signature was saved for this approval.",
        loading: "Loading drafts...",
        orderNotFound: "Order not found.",
        loadError: "Failed to load drafts. Please check the link.",
    },
    zh: {
        title: "项目图稿",
        subtitle: "查看并审核项目图稿",
        order: "订单",
        customer: "客户",
        approvedOn: "批准日期",
        approveButton: "客户批准",
        approvedButton: "已批准",
        saving: "保存中...",
        approvalSaved: "客户批准已成功保存。",
        approvalSaveFailed: "保存客户批准失败，请重试。",
        checkAllDrafts: "请先勾选所有图稿后再批准。",
        signatureRequired: "请先签名后再批准。",
        approvalRequirements: "批准要求",
        tickAllDrafts: "勾选所有图稿",
        provideSignature: "提供客户签名",
        draftFiles: "图稿文件",
        file: "个文件",
        files: "个文件",
        noProductLinked: "未关联产品",
        noDraftsTitle: "暂无图稿",
        noDraftsSubtitle: "此订单尚未上传任何图稿。",
        signatureTitle: "客户签名",
        savedSignatureTitle: "已保存签名",
        clearSignature: "清除签名",
        signatureHint: "请使用鼠标或触摸在上方签名后再批准。",
        noSavedSignature: "此批准未保存签名。",
        loading: "正在加载图稿...",
        orderNotFound: "未找到订单。",
        loadError: "加载图稿失败，请检查链接。",
    },
};

export default function PublicBlueprint() {
    const { id: orderId } = useParams();
    const [orderData, setOrderData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isApproving, setIsApproving] = useState(false);
    const [approvalMessage, setApprovalMessage] = useState("");
    const [checkedDrafts, setCheckedDrafts] = useState({});
    const [hasSigned, setHasSigned] = useState(false);
    const [language, setLanguage] = useState("en");
    const [lightboxUrl, setLightboxUrl] = useState(null);
    const [lightboxAlt, setLightboxAlt] = useState("");
    const signatureCanvasRef = useRef(null);
    const drawingRef = useRef(false);
    const t = translations[language];

    const closeLightbox = useCallback(() => {
        setLightboxUrl(null);
        setLightboxAlt("");
    }, []);

    useEffect(() => {
        if (!lightboxUrl) return;
        const handleKey = (e) => { if (e.key === "Escape") closeLightbox(); };
        document.addEventListener("keydown", handleKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", handleKey);
            document.body.style.overflow = "";
        };
    }, [lightboxUrl, closeLightbox]);

    useEffect(() => {
        const fetchOrder = async () => {
            if (!orderId) return;
            setLoading(true);
            setError(null);
            try {
                const res = await publicRequest.get(`/orders/findOrder/${orderId}`);
                setOrderData(res.data);
                setCheckedDrafts({});
                setHasSigned(false);
            } catch (err) {
                console.error("Error fetching order:", err);
                setError(
                    err?.response?.status === 404
                        ? t.orderNotFound
                        : t.loadError
                );
            } finally {
                setLoading(false);
            }
        };
        fetchOrder();
    }, [orderId, t.orderNotFound, t.loadError]);

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner} />
                <p style={styles.loadingText}>{t.loading}</p>
            </div>
        );
    }

    if (error || !orderData) {
        return (
            <div style={styles.loadingContainer}>
                <p style={{ color: "#ef4444", fontSize: "16px", textAlign: "center", padding: "0 16px" }}>
                    {error || t.orderNotFound}
                </p>
            </div>
        );
    }

    const blueprints = orderData?.blueprint || [];
    const orderId_display = orderData?.coNumber || orderData?.invoiceId || "N/A";
    const isCustomerApproved = Boolean(orderData?.customerApprovedDate);
    const areAllDraftsChecked =
        blueprints.length > 0 &&
        blueprints.every((_, index) => checkedDrafts[index]);

    const toggleDraftCheck = (index) => {
        setCheckedDrafts((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };

    const clearSignature = () => {
        const canvas = signatureCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasSigned(false);
    };

    const getPointFromEvent = (canvas, event) => {
        const rect = canvas.getBoundingClientRect();
        const source = event.touches ? event.touches[0] : event;
        return {
            x: (source.clientX - rect.left) * (canvas.width / rect.width),
            y: (source.clientY - rect.top) * (canvas.height / rect.height),
        };
    };

    const startDrawing = (event) => {
        const canvas = signatureCanvasRef.current;
        if (!canvas || isCustomerApproved) return;
        const ctx = canvas.getContext("2d");
        const { x, y } = getPointFromEvent(canvas, event);
        drawingRef.current = true;
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const drawSignature = (event) => {
        const canvas = signatureCanvasRef.current;
        if (!canvas || !drawingRef.current || isCustomerApproved) return;
        if (event.touches) {
            event.preventDefault();
        }
        const ctx = canvas.getContext("2d");
        const { x, y } = getPointFromEvent(canvas, event);
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.strokeStyle = "#000";
        ctx.lineTo(x, y);
        ctx.stroke();
        setHasSigned(true);
    };

    const stopDrawing = () => {
        drawingRef.current = false;
    };

    const handleCustomerApprove = async () => {
        if (!orderData?._id || isApproving || isCustomerApproved) return;
        if (!areAllDraftsChecked) {
            setApprovalMessage(t.checkAllDrafts);
            return;
        }

        const signatureCanvas = signatureCanvasRef.current;
        if (!signatureCanvas || !hasSigned) {
            setApprovalMessage(t.signatureRequired);
            return;
        }

        const signatureDataUrl = signatureCanvas.toDataURL("image/png");

        try {
            setIsApproving(true);
            setApprovalMessage("");
            const payload = {
                commenceDate: new Date().toISOString().split("T")[0],
                customerApprovedBy:
                    typeof orderData?.userId === "object"
                        ? orderData?.userId?._id
                        : orderData?.userId,
                customerApprovalSignature: signatureDataUrl,
            };

            const res = await publicRequest.put(
                `/orders/customerApproved/${orderData._id}`,
                payload
            );

            setOrderData(res.data);
            setApprovalMessage(t.approvalSaved);
        } catch (err) {
            console.error("Error saving customer approval:", err);
            setApprovalMessage(t.approvalSaveFailed);
        } finally {
            setIsApproving(false);
        }
    };

    return (
        <div style={styles.pageWrapper}>
            <div style={styles.container}>
                {/* Header */}
                <div style={styles.headerRow}>
                    <div style={styles.header}>
                        <h1 style={styles.title}>{t.title}</h1>
                        <p style={styles.subtitle}>{t.subtitle}</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setLanguage(language === "en" ? "zh" : "en")}
                        style={styles.languageButton}
                    >
                        <FiGlobe style={{ width: 18, height: 18 }} />
                        <span>{language === "en" ? "中文" : "English"}</span>
                    </button>
                </div>

                {/* Order Info */}
                <div style={styles.orderCard}>
                    <div style={styles.orderIconWrapper}>
                        <FiUpload style={{ width: 20, height: 20, color: "#fff" }} />
                    </div>
                    <div style={styles.orderInfoContent}>
                        <p style={styles.orderNumber}>
                            {t.order}: {orderId_display}
                        </p>
                        <p style={styles.customerName}>
                            {t.customer}: {orderData?.userId?.name || "N/A"}
                        </p>
                        {isCustomerApproved && (
                            <p style={styles.approvedText}>
                                {t.approvedOn} {orderData?.customerApprovedDate}
                            </p>
                        )}
                    </div>
                    <div style={styles.orderActions}>
                        <button
                            type="button"
                            style={{
                                ...styles.approveButton,
                                ...(isCustomerApproved ? styles.approveButtonDone : {}),
                                ...(isApproving ? styles.approveButtonLoading : {}),
                            }}
                            onClick={handleCustomerApprove}
                            disabled={
                                isApproving ||
                                isCustomerApproved ||
                                !areAllDraftsChecked ||
                                !hasSigned
                            }
                        >
                            <span style={styles.labelWithTag}>
                                <StepCodeTag code={CUSTOMER_DRAFT_STEP_CODES.customerApprove} />
                                {isCustomerApproved
                                    ? t.approvedButton
                                    : isApproving
                                      ? t.saving
                                      : t.approveButton}
                            </span>
                        </button>
                    </div>
                </div>
                {!isCustomerApproved && (
                    <div style={styles.requirementsCard}>
                        <p style={styles.requirementsTitle}>{t.approvalRequirements}</p>
                        <p style={styles.requirementItem}>
                            {areAllDraftsChecked ? "✓" : "○"} {t.tickAllDrafts}
                        </p>
                        <p style={styles.requirementItem}>
                            {hasSigned ? "✓" : "○"} {t.provideSignature}
                        </p>
                    </div>
                )}
                {approvalMessage && (
                    <p
                        style={{
                            ...styles.approvalMessage,
                            color: approvalMessage.includes("Failed")
                                ? "#dc2626"
                                : "#059669",
                        }}
                    >
                        {approvalMessage}
                    </p>
                )}

                {/* Draft Files Section */}
                <div style={styles.sectionCard}>
                    <div style={styles.sectionHeader}>
                        <div style={styles.sectionHeaderLeft}>
                            <FiUpload style={{ width: 18, height: 18 }} />
                            <span style={styles.sectionTitle}>{t.draftFiles}</span>
                        </div>
                        <span style={styles.fileCount}>
                            {blueprints.length} {blueprints.length !== 1 ? t.files : t.file}
                        </span>
                    </div>

                    {blueprints.length > 0 ? (
                        <div style={styles.fileList}>
                            {blueprints.map((bp, index) => {
                                const isImage = /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i.test(bp.url || "");
                                return (
                                    <div key={bp._id || index} style={styles.fileCard}>
                                        {!isCustomerApproved && (
                                            <label style={styles.tickLabel}>
                                                <StepCodeTag code={CUSTOMER_DRAFT_STEP_CODES.draftSelection} />
                                                <input
                                                    type="checkbox"
                                                    checked={!!checkedDrafts[index]}
                                                    onChange={() => toggleDraftCheck(index)}
                                                    aria-label={`Draft ${index + 1}`}
                                                    style={styles.tickInput}
                                                />
                                            </label>
                                        )}
                                        <div style={styles.fileCardInner}>
                                            {/* Preview */}
                                            <div
                                                style={{
                                                    ...styles.previewArea,
                                                    cursor: isImage ? "pointer" : "default",
                                                    position: "relative",
                                                }}
                                                onClick={() => {
                                                    if (isImage) {
                                                        setLightboxUrl(bp.url);
                                                        setLightboxAlt(bp.name || `Draft ${index + 1}`);
                                                    }
                                                }}
                                            >
                                                {isImage ? (
                                                    <>
                                                        <img
                                                            src={bp.url}
                                                            alt={bp.name || `Draft ${index + 1}`}
                                                            style={styles.previewImage}
                                                            onError={(e) => {
                                                                e.target.style.display = "none";
                                                                e.target.nextSibling && (e.target.nextSibling.style.display = "flex");
                                                            }}
                                                        />
                                                        <div style={{ ...styles.previewFallback, display: "none" }}>
                                                            <FiImage style={{ width: 28, height: 28, color: "#9ca3af" }} />
                                                        </div>
                                                        <div style={styles.zoomOverlay}>
                                                            <FiZoomIn style={{ width: 20, height: 20, color: "#fff" }} />
                                                        </div>
                                                    </>
                                                ) : (
                                                    <a
                                                        href={bp.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={styles.previewFallback}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <FiFile style={{ width: 28, height: 28, color: "#6b7280" }} />
                                                    </a>
                                                )}
                                            </div>

                                            {/* Details */}
                                            <div style={styles.fileDetails}>
                                                <p style={styles.fileName}>{bp.name || "Untitled"}</p>
                                                {bp.itemName ? (
                                                    <span style={styles.productBadge}>{bp.itemName}</span>
                                                ) : (
                                                    <span style={styles.noProduct}>{t.noProductLinked}</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* File number */}
                                        <span style={styles.fileIndex}>{index + 1}</span>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div style={styles.emptyState}>
                            <div style={styles.emptyIcon}>
                                <FiUpload style={{ width: 32, height: 32, color: "#9ca3af" }} />
                            </div>
                            <p style={styles.emptyTitle}>{t.noDraftsTitle}</p>
                            <p style={styles.emptySubtitle}>
                                {t.noDraftsSubtitle}
                            </p>
                        </div>
                    )}
                </div>

                {!isCustomerApproved && (
                    <div style={styles.signatureCard}>
                        <div style={styles.signatureHeader}>
                            <p style={styles.signatureTitle}>
                                <span style={styles.labelWithTag}>
                                    <StepCodeTag code={CUSTOMER_DRAFT_STEP_CODES.customerSignature} />
                                    {t.signatureTitle}
                                </span>
                            </p>
                            <button
                                type="button"
                                onClick={clearSignature}
                                style={styles.clearButton}
                            >
                                {t.clearSignature}
                            </button>
                        </div>
                        <canvas
                            ref={signatureCanvasRef}
                            width={640}
                            height={180}
                            style={styles.signatureCanvas}
                            onMouseDown={startDrawing}
                            onMouseMove={drawSignature}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                            onTouchStart={startDrawing}
                            onTouchMove={drawSignature}
                            onTouchEnd={stopDrawing}
                        />
                        <p style={styles.signatureHint}>
                            {t.signatureHint}
                        </p>
                    </div>
                )}
                {isCustomerApproved && (
                    <div style={styles.signatureCard}>
                        <div style={styles.signatureHeader}>
                            <p style={styles.signatureTitle}>{t.savedSignatureTitle}</p>
                        </div>
                        {orderData?.customerApprovalSignature ? (
                            <div style={styles.savedSignatureWrap}>
                                <img
                                    src={orderData.customerApprovalSignature}
                                    alt={t.savedSignatureTitle}
                                    style={styles.savedSignatureImage}
                                />
                            </div>
                        ) : (
                            <p style={styles.noSavedSignatureText}>{t.noSavedSignature}</p>
                        )}
                    </div>
                )}
            </div>

            {/* Lightbox */}
            {lightboxUrl && (
                <div style={styles.lightboxOverlay} onClick={closeLightbox}>
                    <button style={styles.lightboxClose} onClick={closeLightbox}>
                        <FiX style={{ width: 24, height: 24 }} />
                    </button>
                    <img
                        src={lightboxUrl}
                        alt={lightboxAlt}
                        style={styles.lightboxImage}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </div>
    );
}

const styles = {
    headerRow: {
        marginBottom: "28px",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "12px",
    },
    pageWrapper: {
        minHeight: "100vh",
        backgroundColor: "#f3f4f6",
        padding: "32px 16px",
        display: "flex",
        justifyContent: "center",
    },
    container: {
        width: "100%",
        maxWidth: "720px",
    },
    header: {
        marginBottom: 0,
    },
    title: {
        fontSize: "28px",
        fontWeight: "700",
        color: "#111827",
        margin: "0 0 6px 0",
    },
    titleWithTag: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        flexWrap: "wrap",
    },
    labelWithTag: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        flexWrap: "wrap",
    },
    subtitle: {
        fontSize: "15px",
        color: "#6b7280",
        margin: 0,
    },
    languageButton: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        border: "1px solid #d1d5db",
        borderRadius: "10px",
        padding: "8px 12px",
        backgroundColor: "#fff",
        color: "#374151",
        fontSize: "14px",
        fontWeight: 600,
        cursor: "pointer",
    },
    orderCard: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
        backgroundColor: "#fff",
        borderRadius: "14px",
        padding: "20px 24px",
        marginBottom: "24px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        border: "1px solid #e5e7eb",
    },
    orderIconWrapper: {
        width: "48px",
        height: "48px",
        borderRadius: "50%",
        /* Matches orderList.css .action-icon-blueprint / .modern-button-success scheme */
        background: "linear-gradient(135deg, #10b981, #059669)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    orderInfoContent: {
        flex: 1,
    },
    orderActions: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
    },
    orderNumber: {
        fontSize: "18px",
        fontWeight: "700",
        color: "#111827",
        margin: "0 0 3px 0",
    },
    customerName: {
        fontSize: "14px",
        color: "#6b7280",
        margin: 0,
    },
    approvedText: {
        fontSize: "13px",
        color: "#059669",
        margin: "6px 0 0 0",
        fontWeight: 600,
    },
    approveButton: {
        display: "inline-flex",
        alignItems: "center",
        border: "none",
        borderRadius: "10px",
        padding: "10px 16px",
        background: "linear-gradient(135deg, #10b981, #059669)",
        color: "#fff",
        fontSize: "14px",
        fontWeight: 700,
        cursor: "pointer",
        transition: "opacity 0.2s ease",
    },
    approveButtonLoading: {
        opacity: 0.85,
        cursor: "wait",
    },
    approveButtonDone: {
        background: "#6b7280",
        cursor: "not-allowed",
    },
    approvalMessage: {
        margin: "10px 4px 16px 4px",
        fontSize: "13px",
        fontWeight: 600,
    },
    requirementsCard: {
        backgroundColor: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        padding: "12px 16px",
        margin: "0 0 16px 0",
    },
    requirementsTitle: {
        margin: "0 0 8px 0",
        fontSize: "14px",
        fontWeight: 700,
        color: "#111827",
    },
    requirementItem: {
        margin: "4px 0",
        fontSize: "13px",
        color: "#374151",
    },
    sectionCard: {
        backgroundColor: "#fff",
        borderRadius: "14px",
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        border: "1px solid #e5e7eb",
    },
    sectionHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px 24px",
        backgroundColor: "#10b981",
        color: "#fff",
    },
    sectionHeaderLeft: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
    },
    sectionTitle: {
        fontSize: "17px",
        fontWeight: "700",
    },
    fileCount: {
        fontSize: "14px",
        opacity: 0.9,
    },
    fileList: {
        padding: "10px",
    },
    fileCard: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px",
        borderRadius: "12px",
        border: "1px solid #f3f4f6",
        marginBottom: "8px",
        transition: "background-color 0.15s ease",
        backgroundColor: "#fafafa",
    },
    tickLabel: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        marginLeft: "4px",
        marginRight: "12px",
        whiteSpace: "nowrap",
        flexShrink: 0,
    },
    tickInput: {
        width: "16px",
        height: "16px",
        cursor: "pointer",
    },
    fileCardInner: {
        display: "flex",
        alignItems: "center",
        gap: "16px",
        flex: 1,
        minWidth: 0,
    },
    previewArea: {
        width: "130px",
        height: "130px",
        borderRadius: "10px",
        overflow: "hidden",
        flexShrink: 0,
        backgroundColor: "#f9fafb",
        border: "1px solid #e5e7eb",
    },
    previewImage: {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
    },
    previewFallback: {
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f3f4f6",
    },
    fileDetails: {
        flex: 1,
        minWidth: 0,
    },
    fileName: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#111827",
        margin: "0 0 8px 0",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    productBadge: {
        display: "inline-block",
        fontSize: "13px",
        color: "#4b5563",
        backgroundColor: "#f3f4f6",
        padding: "3px 12px",
        borderRadius: "20px",
        border: "1px solid #e5e7eb",
    },
    noProduct: {
        fontSize: "13px",
        color: "#9ca3af",
        fontStyle: "italic",
    },
    fileIndex: {
        fontSize: "14px",
        fontWeight: "600",
        color: "#9ca3af",
        marginLeft: "14px",
        flexShrink: 0,
    },
    emptyState: {
        padding: "56px 28px",
        textAlign: "center",
    },
    emptyIcon: {
        width: "72px",
        height: "72px",
        borderRadius: "50%",
        backgroundColor: "#f3f4f6",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 18px auto",
    },
    emptyTitle: {
        fontSize: "18px",
        fontWeight: "600",
        color: "#111827",
        margin: "0 0 8px 0",
    },
    emptySubtitle: {
        fontSize: "14px",
        color: "#9ca3af",
        margin: 0,
    },
    signatureCard: {
        marginTop: "16px",
        backgroundColor: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: "14px",
        padding: "16px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    },
    signatureHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: "10px",
    },
    signatureTitle: {
        margin: 0,
        fontSize: "15px",
        fontWeight: 700,
        color: "#111827",
    },
    clearButton: {
        display: "inline-flex",
        alignItems: "center",
        border: "1px solid #fecaca",
        borderRadius: "8px",
        backgroundColor: "#fef2f2",
        color: "#b91c1c",
        fontSize: "12px",
        fontWeight: 600,
        padding: "6px 10px",
        cursor: "pointer",
    },
    signatureCanvas: {
        width: "100%",
        height: "180px",
        border: "1px solid #d1d5db",
        borderRadius: "10px",
        backgroundColor: "#fff",
        touchAction: "none",
        cursor: "crosshair",
    },
    signatureHint: {
        margin: "8px 0 0 0",
        fontSize: "12px",
        color: "#6b7280",
        textAlign: "center",
    },
    savedSignatureWrap: {
        border: "1px solid #d1d5db",
        borderRadius: "10px",
        backgroundColor: "#f9fafb",
        padding: "12px",
        minHeight: "180px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    savedSignatureImage: {
        maxWidth: "100%",
        maxHeight: "160px",
        objectFit: "contain",
    },
    noSavedSignatureText: {
        margin: 0,
        fontSize: "13px",
        color: "#6b7280",
        textAlign: "center",
    },
    loadingContainer: {
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f3f4f6",
        gap: "18px",
    },
    loadingText: {
        fontSize: "16px",
        color: "#6b7280",
        margin: 0,
    },
    spinner: {
        width: "36px",
        height: "36px",
        border: "3px solid #e5e7eb",
        borderTopColor: "#10b981",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
    },
    zoomOverlay: {
        position: "absolute",
        bottom: "6px",
        right: "6px",
        width: "30px",
        height: "30px",
        borderRadius: "50%",
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: 0.7,
        transition: "opacity 0.15s ease",
        pointerEvents: "none",
    },
    lightboxOverlay: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "24px",
        boxSizing: "border-box",
    },
    lightboxClose: {
        position: "absolute",
        top: "16px",
        right: "16px",
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        border: "none",
        backgroundColor: "rgba(255,255,255,0.15)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "background-color 0.2s ease",
        zIndex: 10000,
    },
    lightboxImage: {
        maxWidth: "90vw",
        maxHeight: "90vh",
        objectFit: "contain",
        borderRadius: "8px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
    },
};
